class ProductGalleryCarouselPagination extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('.product-gallery-carousel')?.querySelector('slider-component');
    if (!this.sliderComponent) return;

    this.slider = this.sliderComponent.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.onScroll = this.updateActivePage.bind(this);
    this.onResize = this.buildPagination.bind(this);

    this.slider.addEventListener('scroll', this.onScroll);
    this.sliderComponent.addEventListener('slideChanged', this.onScroll);

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.slider);

    this.buildPagination();
  }

  disconnectedCallback() {
    this.slider?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  getVisibleSlides() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter(
      (element) => element.clientWidth > 0
    );
  }

  getPageCount() {
    const slides = this.getVisibleSlides();
    if (slides.length < 2) return slides.length > 0 ? 1 : 0;

    const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
    if (!slideOffset) return 1;

    const slidesPerPage = Math.floor(
      (this.slider.clientWidth - slides[0].offsetLeft) / slideOffset
    );

    return Math.max(1, slides.length - slidesPerPage + 1);
  }

  getCurrentPage() {
    const slides = this.getVisibleSlides();
    if (slides.length < 2) return 1;

    const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
    if (!slideOffset) return 1;

    return Math.round(this.slider.scrollLeft / slideOffset) + 1;
  }

  buildPagination() {
    const pageCount = this.getPageCount();
    this.innerHTML = '';

    if (pageCount <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    for (let page = 1; page <= pageCount; page += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-gallery-carousel__pagination-square';
      button.dataset.page = page;
      button.setAttribute('aria-label', `Go to slide group ${page} of ${pageCount}`);
      button.addEventListener('click', () => this.goToPage(page));
      this.appendChild(button);
    }

    this.updateActivePage();
  }

  goToPage(page) {
    const slides = this.getVisibleSlides();
    if (slides.length < 2) return;

    const slideOffset = slides[1].offsetLeft - slides[0].offsetLeft;
    if (!slideOffset) return;

    this.slider.scrollTo({
      left: (page - 1) * slideOffset,
      behavior: 'smooth',
    });
  }

  updateActivePage() {
    const currentPage = this.getCurrentPage();

    this.querySelectorAll('.product-gallery-carousel__pagination-square').forEach((button) => {
      const isActive = parseInt(button.dataset.page, 10) === currentPage;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

customElements.define('product-gallery-carousel-pagination', ProductGalleryCarouselPagination);

class GalleryCarouselVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.userPaused = false;
    this.video.muted = true;
    this.video.loop = true;
    this.video.playsInline = true;
    this.video.removeAttribute('controls');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');

    this.onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.togglePlayback();
    };

    this.addEventListener('click', this.onClick);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.userPaused) {
            this.video.play().catch(() => {});
          } else if (!entry.isIntersecting) {
            this.video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    this.observer.observe(this);

    if (this.isVisible()) {
      this.video.play().catch(() => {});
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  isVisible() {
    const rect = this.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
  }

  togglePlayback() {
    if (this.video.paused) {
      this.userPaused = false;
      this.video.play().catch(() => {});
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }
}

customElements.define('gallery-carousel-video', GalleryCarouselVideo);
